// OPL (Object-Process Language) 语言生成器
import { getEdgeText } from './utils/edgeHelpers';

export interface OPLStatement {
  id: string;
  type: 'node' | 'edge' | 'relation';
  text: string;
  elementIds: string[];
  nodeId?: string;
  edgeId?: string;
  description?: string;
}

export class OPLGenerator {
  private nodes: any[] = [];
  private edges: any[] = [];

  constructor(graphData: { nodes: any[]; edges: any[] }) {
    this.nodes = graphData.nodes || [];
    this.edges = graphData.edges || [];
  }

  // 生成所有 OPL 语句
  generateOPLStatements(): OPLStatement[] {
    const statements: OPLStatement[] = [];

    console.log(
      '🔤 开始生成OPL语句，节点数:',
      this.nodes.length,
      '边数:',
      this.edges.length
    );

    // 1. 为每个节点生成语句
    this.nodes.forEach(node => {
      const nodeTypeName = this.getNodeTypeName(node.type);
      const nodeName = this.getDisplayName(node);

      statements.push({
        id: `node_${node.id}`,
        text: `创建了一个${nodeTypeName}--名称 ${nodeName}`,
        type: 'node',
        elementIds: [node.id],
        nodeId: node.id,
        description: `声明 ${nodeName} 为${nodeTypeName}`,
      });
    });

    // 2. 为每个边生成连接语句
    this.edges.forEach(edge => {
      const sourceNode = this.nodes.find(n => n.id === edge.sourceNodeId);
      const targetNode = this.nodes.find(n => n.id === edge.targetNodeId);

      if (sourceNode && targetNode) {
        const sourceTypeName = this.getNodeTypeName(sourceNode.type);
        const targetTypeName = this.getNodeTypeName(targetNode.type);
        const sourceName = this.getDisplayName(sourceNode);
        const targetName = this.getDisplayName(targetNode);

        // 获取边的文本
        const edgeText = this.getEdgeTextValue(edge);
        const connectionText = edgeText
          ? `${sourceTypeName}${sourceName} 通过"${edgeText}" 连接到 ${targetTypeName}${targetName}`
          : `${sourceTypeName}${sourceName} 连接到 ${targetTypeName}${targetName}`;

        statements.push({
          id: `edge_${edge.id}`,
          text: connectionText,
          type: 'edge',
          elementIds: [edge.sourceNodeId, edge.targetNodeId, edge.id],
          edgeId: edge.id,
          description: `描述连接关系`,
        });
      }
    });

    console.log('🔤 OPL语句生成完成，共生成:', statements.length, '条语句');
    return statements;
  }

  // 节点类型配置
  private nodeTypes = [
    { type: 'rect', name: '实体', icon: '📦', color: '#1890ff' },
    { type: 'circle', name: '属性', icon: '⚪', color: '#52c41a' },
    { type: 'diamond', name: '关系', icon: '💎', color: '#fa8c16' },
    { type: 'polygon', name: '过程', icon: '📐', color: '#722ed1' },
    { type: 'dynamic-group', name: '实体', icon: '🗂️', color: '#13c2c2' },
    { type: 'state', name: '状态', icon: '🔘', color: '#1890ff' },
  ];

  // 获取节点类型名称
  private getNodeTypeName(type: string): string {
    const nodeConfig = this.nodeTypes.find(n => n.type === type);
    return nodeConfig?.name || '未知';
  }

  // 获取节点显示名称
  private getDisplayName(node: any): string {
    // 如果 text 是字符串，直接返回
    if (typeof node.text === 'string') {
      return node.text;
    }

    // 如果 text 是对象，尝试获取 value 属性
    if (node.text && typeof node.text === 'object' && node.text.value) {
      return node.text.value;
    }

    // 尝试从 properties.name 获取
    if (node.properties?.name && typeof node.properties.name === 'string') {
      return node.properties.name;
    }

    // 最后返回 id 或默认名称
    return node.id || '未命名节点';
  }

  // 获取边的文字值（使用工具函数）
  private getEdgeTextValue(edge: any): string {
    const text = getEdgeText(edge);

    console.log('获取边文字:', {
      edgeId: edge.id,
      rawText: edge.text,
      propertiesText: edge.properties?.text,
      finalText: text,
    });

    return text;
  }

  // 生成完整的 OPL 文档
  generateOPLDocument(): string {
    const statements = this.generateOPLStatements();

    if (statements.length === 0) {
      return '// No OPL statements generated. Please add nodes and connections to the diagram.';
    }

    let document = '// Object-Process Language (OPL) Statements\n';
    document += '// Generated automatically from the OPM diagram\n\n';

    // 按类型分组
    const nodeStatements = statements.filter(s => s.type === 'node');
    const edgeStatements = statements.filter(s => s.type === 'edge');
    const relationStatements = statements.filter(s => s.type === 'relation');

    if (nodeStatements.length > 0) {
      document += '// Entity Declarations\n';
      nodeStatements.forEach(stmt => {
        // 从语句文本中提取节点名称
        const match = stmt.text.match(/创建了一个.*--名称 (.+)/);
        const nodeName = match ? match[1] : 'Object';
        document += `[object ${nodeName}] is an entity.\n`;
      });
      document += '\n';
    }

    if (edgeStatements.length > 0) {
      document += '// Connection Relationships\n';
      edgeStatements.forEach(stmt => {
        document += `// ${stmt.text}\n`;
      });
      document += '\n';
    }

    if (relationStatements.length > 0) {
      document += '// Other Relationships\n';
      relationStatements.forEach(stmt => {
        document += `${stmt.text}\n`;
      });
      document += '\n';
    }

    return document;
  }

  // 根据元素添加历史生成OPL语句（按添加顺序）
  generateOPLStatementsWithHistory(
    elementAddHistory: Array<{
      id: string;
      type: 'node' | 'edge';
      timestamp: number;
    }>
  ): OPLStatement[] {
    const statements: OPLStatement[] = [];

    console.log(
      '🔤 开始根据历史记录生成OPL语句，节点数:',
      this.nodes.length,
      '边数:',
      this.edges.length,
      '历史记录数:',
      elementAddHistory.length
    );

    // 使用添加历史来确定顺序
    const allElements: Array<{
      element: any;
      type: 'node' | 'edge';
      timestamp: number;
    }> = [];

    // 按照添加历史的顺序处理元素
    elementAddHistory.forEach(historyItem => {
      if (historyItem.type === 'node') {
        const node = this.nodes.find(n => n.id === historyItem.id);
        if (node) {
          allElements.push({
            element: node,
            type: 'node',
            timestamp: historyItem.timestamp,
          });
        }
      } else if (historyItem.type === 'edge') {
        const edge = this.edges.find(e => e.id === historyItem.id);
        if (edge) {
          allElements.push({
            element: edge,
            type: 'edge',
            timestamp: historyItem.timestamp,
          });
        }
      }
    });

    // 添加历史中没有记录的元素（可能是通过其他方式添加的）
    this.nodes.forEach(node => {
      if (!elementAddHistory.find(h => h.id === node.id)) {
        allElements.push({
          element: node,
          type: 'node',
          timestamp: Date.now(), // 使用当前时间作为备用
        });
      }
    });

    this.edges.forEach(edge => {
      if (!elementAddHistory.find(h => h.id === edge.id)) {
        allElements.push({
          element: edge,
          type: 'edge',
          timestamp: Date.now(), // 使用当前时间作为备用
        });
      }
    });

    // 按时间戳排序（虽然添加历史已经是有序的，但为了保险起见）
    allElements.sort((a, b) => a.timestamp - b.timestamp);

    console.log(
      'OPL语句生成顺序（基于添加历史）:',
      allElements.map(e => ({
        id: e.element.id,
        type: e.type,
        timestamp: e.timestamp,
      }))
    );

    // 按排序后的顺序生成语句
    allElements.forEach(({ element, type }) => {
      if (type === 'node') {
        const nodeTypeName = this.getNodeTypeName(element.type);
        const nodeName = this.getDisplayName(element);
        statements.push({
          id: `node_${element.id}`,
          text: `创建了一个${nodeTypeName}--名称 ${nodeName}`,
          type: 'node',
          elementIds: [element.id],
          nodeId: element.id,
          description: `声明 ${nodeName} 为${nodeTypeName}`,
        });
      } else if (type === 'edge') {
        const sourceNode = this.nodes.find(n => n.id === element.sourceNodeId);
        const targetNode = this.nodes.find(n => n.id === element.targetNodeId);

        if (sourceNode && targetNode) {
          const sourceTypeName = this.getNodeTypeName(sourceNode.type);
          const targetTypeName = this.getNodeTypeName(targetNode.type);
          const sourceName = this.getDisplayName(sourceNode);
          const targetName = this.getDisplayName(targetNode);

          // 获取边的文本
          const edgeText = this.getEdgeTextValue(element);
          const connectionText = edgeText
            ? `${sourceTypeName}${sourceName} 通过"${edgeText}" 连接到 ${targetTypeName}${targetName}`
            : `${sourceTypeName}${sourceName} 连接到 ${targetTypeName}${targetName}`;

          statements.push({
            id: `edge_${element.id}`,
            text: connectionText,
            type: 'edge',
            elementIds: [
              element.sourceNodeId,
              element.targetNodeId,
              element.id,
            ],
            edgeId: element.id,
            description: `描述连接关系`,
          });
        }
      }
    });

    console.log(
      '🔤 基于历史记录的OPL语句生成完成，共生成:',
      statements.length,
      '条语句'
    );
    return statements;
  }
}
